<template>
  <div class="page page-van">
    <!-- HEADER -->
    <site-header is-static />

    <v-main>
      <!-- MAIN -->
      <div
        v-if="!isLoading"
        class="app-content site-width"
      >
        <custom-validation-observer
          ref="formValidationObserver"
          v-slot="formObserver"
          tag="div"
          class="form-van-wrapper"
          @valid="onValid"
        >
          <!-- BREADCRUMBS -->
          <steps
            v-if="$vuetify.breakpoint.smAndDown"
            :steps="formConfigData"
            @go-to-step="(val) => onGoToStep({...val,formObserver})"
          />

          <!-- CTA -->
          <div class="app-content__spacer">
            <qz-cta
              class="page-van-form__cta"
              :title="activeStepTitle"
              :current-step="currentStep"
            >
              <template
                v-if="isAdditionalDriver"
                #actions
              >
                Let us know {{ form.drivers[currentDriver].firstName ? form.drivers[currentDriver].firstName + "'s" : "their" }} details or <a
                  href="#"
                  @click.prevent="cancelEditingDriver"
                >cancel editing</a> this driver.
              </template>
              <template #firstStepInfo>
                <p>
                  <strong>Let's save some money!</strong>
                  Take care to answer questions accurately. Incomplete information could see your policy cancelled or a claim rejected.
                </p>
              </template>
            </qz-cta>

            <div
              class="van-form d-block d-md-flex"
              sticky-container
            >
              <aside
                class="
                  van-form__sidebar
                  d-none
                  d-md-block
                "
              >
                <div v-sticky="$vuetify.breakpoint.mdAndUp">
                  <list-steps
                    :steps="formConfigData"
                    :form-observer="patchObserver(formObserver)"
                    :current-step="currentStep"
                    :current-driver="currentDriver"
                    @go-to-group="onGoToGroup"
                    @go-to-step="onGoToStep"
                  />
                  <claims-form-link />
                </div>
              </aside>
              <div class="van-form__content ml-md-6">
                <form
                  id="form"
                  novalidate
                  class="form form-van"
                  @submit.prevent
                  @keydown.enter.prevent
                >
                  <!-- FORM STEPS -->
                  <component
                    :is="stepComponent"
                    :key="componentKey"
                    ref="step"
                    :form="form"
                    :driver-index="currentDriver"
                    :form-observer="patchObserver(formObserver)"
                    :step-structure="vanStructure[currentStep - 1]"
                    :show-confirm-modal="showConfirmModal"
                    :drivers-backup="driversBackup"
                    class="form-van__step"
                    @backup-additional-drivers="driversBackup = $event"
                    @add-driver="onBeforeDriverAdd(formObserver)"
                    @change-driver="
                      formObserver.reset();
                      isNewDriver = false;
                      changeDriver($event);
                    "
                    @delete-driver="deleteDriver"
                    @toast="showToast"
                    @show-modal="showConfirmModal"
                    @no-continue="noContinue = $event"
                    @loading="isProcessing = $event"
                    @step-data="stepData = $event"
                    @reset-observer="resetObserver"
                    @tracking="gtmTracking"
                    @restore-additional-drivers="restoreAdditionalDrivers"
                    @remove-additional-drivers="removeAdditionalDrivers"
                  />

                  <!-- ERRORS SECTION -->
                  <core-step-errors
                    :is-additional-driver="isAdditionalDriver"
                    :component-key="componentKey"
                    :active-step="activeStep"
                    :structure="vanStructure"
                    :form-observer="formObserver"
                    :is-validated="isValidated"
                    :step-ref="$refs.step"
                  />

                  <qz-form-actions
                    ref="formActions"
                    :current-step="currentStep"
                    :form-observer="formObserver"
                    :is-additional-driver="isAdditionalDriver"
                    :component-key="componentKey"
                    :show-actions="showActions"
                    :is-last-step="isLastStep"
                    @prev-step="prevStep(formObserver)"
                    @next-step="nextStep(formObserver)"
                    @captcha-submit="validateBeforeRecaptcha(formObserver)"
                    @cancel-driver-editing="cancelEditingDriver"
                  />
                </form>
              </div>
            </div>
          </div>
        </custom-validation-observer>
      </div>

      <quote-limit-exceed-popup v-model="quoteLimitExceeded" />

      <!-- FOOTER -->
      <site-footer
        v-if="config.partnerFooter && !isLoading"
        role="footer"
      >
        <div v-html="config.partnerFooterText || ''" />
      </site-footer>

      <branding-logo
        v-if="config.brandingLogo"
        :branding-type="config.brandingType"
      />

      <bespoke-footer-information
        v-if="showBespokeFooterInformation"
        :content="config.bespokeFooterInformation"
      />
    </v-main>

    <!-- LOADING OVERLAY -->
    <page-loader :value="(!isInitialLoaderHidden && isLoading) || isProcessing" />
  </div>
</template>

<script>
import { inject } from '@vue/composition-api';

import useQuotezoneCommon from '@/composables/useQuotezoneCommon';
import useQuotezoneForm from '@/composables/useQuotezoneForm';
import { getQuotePayload } from '@/payloadTemplates/van';
import { vanStructure } from '@/helpers/products/van';

import Steps from '@/components/Steps/Steps';

import Step1 from '@/views/van/steps/Step1.vue';
import Step2 from '@/views/van/steps/Step2.vue';
import Step3 from '@/views/van/steps/Step3.vue';

import QzCta from '@/components/QzCta/QzCta.vue';
import ListSteps from '@/components/ListSteps/ListSteps.vue';
import ClaimsFormLink from '@/components/ClaimsFormLink.vue';
import QzFormActions from '@/components/QzFormActions.vue';
import CoreStepErrors from '@/components/StepErrors/CoreStepErrors.vue';

export default {
  name: 'page-van-insurance',
  metaInfo () {
    const defaultTitle = `${this.config.partnerText} | Van Insurance`;
    return {
      title: this.config.partnerPageFormTitle || defaultTitle,
    };
  },
  components: {
    CoreStepErrors,
    QzFormActions,
    Step1,
    Step2,
    Step3,
    QzCta,
    ListSteps,
    Steps,
    ClaimsFormLink,
  },
  async created () {
    await this.init();
  },
  setup (props, context) {
    return {
      ...useQuotezoneCommon(null, context),
      ...useQuotezoneForm({
        payload: getQuotePayload(inject('config')),
        structure: vanStructure,
      }, context),
    };
  },
  methods: {
    restoreAdditionalDrivers (drivers) {
      this.form.drivers.push(...drivers);
    },
    removeAdditionalDrivers () {
      this.form.drivers = this.form.drivers.slice(0, 1);
    },
    resetObserver () {
      this.isValidated = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.page-van {
  .app-content {
    &__spacer {
      padding-top: _percentage(35, $site-width);
    }
  }
}

.page-van-form {
  &__cta {
    & + * {
      margin-top: _percentage(35, $site-width);
    }
  }
}

.van-form {
  display: flex;
  align-items: flex-start;
  position: relative;

  &__content {
    flex: 1;
  }

  &__sidebar {
    flex: 0 0 190px;
    width: 190px;
  }
}
</style>

<style lang="scss">
.form-van {
  .form-section + .form-section { margin-top: -1px; }

  &__step {
    border-radius: 6px;
    background: $clr-white;
    border: 1px solid $clr-heather;
    padding: 5px 0;
  }
}
</style>

<style lang="scss" scoped src="@/assets/scss/core-products/product-insurance.scss"/>
<style lang="scss" scoped src="@/assets/scss/page-width.scss"/>
